import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import allergyService from '../../services/allergyService';
import { styled } from '@mui/material/styles';

const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
}));

const AllergiesMetadataViewer = () => {
  const [allergies, setAllergies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRiskLevel, setFilterRiskLevel] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'cards' or 'table'
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [selectedAllergy, setSelectedAllergy] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleViewDetails = (allergy) => {
    setSelectedAllergy(allergy);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedAllergy(null);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [allergiesData, categoriesData] = await Promise.all([
        allergyService.getAllAllergyMetadata(200),
        allergyService.getAllergyCategories()
      ]);
      
      setAllergies(allergiesData);
      setCategories(categoriesData);
    } catch (err) {
      setError('Failed to load allergies metadata: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadData();
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const searchResults = await allergyService.searchAllergyMetadata(searchTerm);
      setAllergies(searchResults);
    } catch (err) {
      setError('Search failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredAllergies = (allergies || []).filter(allergy => {
    const matchesRiskLevel = !filterRiskLevel || allergy.riskLevel === filterRiskLevel;
    const matchesCategory = !filterCategory || 
      (allergy.allergyCategories && Array.isArray(allergy.allergyCategories) && 
       allergy.allergyCategories.some(cat => cat.category === filterCategory));
    
    return matchesRiskLevel && matchesCategory;
  });

  // Paginated data for current page
  const paginatedAllergies = filteredAllergies.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [filterRiskLevel, filterCategory, searchTerm]);

  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const renderCardView = () => (
    <Box>
      <Grid container spacing={3}>
        {paginatedAllergies.map((allergy) => (
        <Grid item xs={12} sm={6} md={4} key={allergy.id}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                  {allergy.drugName}
                </Typography>
                <Chip 
                  label={allergy.riskLevel.toUpperCase()} 
                  color={getRiskLevelColor(allergy.riskLevel)}
                  size="small"
                />
              </Box>
              
              {allergy.rxnormCUI && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  RxNorm CUI: {allergy.rxnormCUI}
                </Typography>
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Source: {allergy.sourceVocabulary} | Type: {allergy.termType}
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Allergy Categories:
                </Typography>
                {allergy.allergyCategories.map((category, index) => (
                  <Accordion key={index} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={category.category} 
                          color={getSeverityColor(category.severity)}
                          size="small"
                        />
                        <Typography variant="body2">
                          {category.description}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Cross-reactive drugs:</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                          {category.crossReactiveDrugs.map((drug, idx) => (
                            <Chip key={idx} label={drug} size="small" variant="outlined" />
                          ))}
                        </Box>
                        
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Common reactions:</strong>
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {category.commonReactions.map((reaction, idx) => (
                            <Chip key={idx} label={reaction} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
              
              <Typography variant="caption" color="text.secondary">
                Data Source: {allergy.dataSource} | Version: {allergy.version}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
      </Grid>
      <TablePagination
        component="div"
        count={filteredAllergies.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20]}
        sx={{ mt: 2 }}
      />
    </Box>
  );

  const renderTableView = () => (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Drug Name</TableCell>
              <TableCell>Risk Level</TableCell>
              <TableCell>Categories</TableCell>
              <TableCell>RxNorm CUI</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAllergies.map((allergy) => (
              <TableRow key={allergy.id}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {allergy.drugName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={allergy.riskLevel.toUpperCase()} 
                    color={getRiskLevelColor(allergy.riskLevel)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {allergy.allergyCategories.map((category, index) => (
                      <Chip 
                        key={index}
                        label={category.category} 
                        color={getSeverityColor(category.severity)}
                        size="small"
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {allergy.rxnormCUI || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {allergy.sourceVocabulary}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => handleViewDetails(allergy)}>
                      <InfoIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredAllergies.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20]}
      />
    </Paper>
  );

  if (loading && allergies.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Allergies Metadata
          </Typography>
        </CardContent>
      </HeaderCard>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Controls */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search drugs or categories"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          sx={{ minWidth: 220 }}
          InputProps={{
            endAdornment: (
              <IconButton size="small" onClick={handleSearch} disabled={loading}>
                <SearchIcon fontSize="small" />
              </IconButton>
            )
          }}
        />
        
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Risk Level</InputLabel>
          <Select
            value={filterRiskLevel}
            label="Risk Level"
            onChange={(e) => setFilterRiskLevel(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={filterCategory}
            label="Category"
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>View</InputLabel>
          <Select
            value={viewMode}
            label="View"
            onChange={(e) => setViewMode(e.target.value)}
          >
            <MenuItem value="cards">Cards</MenuItem>
            <MenuItem value="table">Table</MenuItem>
          </Select>
        </FormControl>
        
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setFilterRiskLevel('');
            setFilterCategory('');
            setSearchTerm('');
            loadData();
          }}
        >
          Clear
        </Button>
      </Box>

      {/* Results Summary */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          {filteredAllergies.length} allergy metadata records found
        </Typography>
        {loading && <CircularProgress size={20} />}
      </Box>

      {/* Results */}
      {filteredAllergies.length === 0 ? (
        <Alert severity="info">
          No allergy metadata found matching your criteria.
        </Alert>
      ) : (
        viewMode === 'cards' ? renderCardView() : renderTableView()
      )}

      {/* Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={handleCloseDetails}
        maxWidth="sm"
        fullWidth
      >
        {selectedAllergy && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="h6">
                    {selectedAllergy.drugName}
                  </Typography>
                  <Chip 
                    label={selectedAllergy.riskLevel.toUpperCase()} 
                    color={getRiskLevelColor(selectedAllergy.riskLevel)}
                    size="small"
                  />
                </Box>
                <IconButton size="small" onClick={handleCloseDetails}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    RxNorm CUI
                  </Typography>
                  <Typography variant="body2">
                    {selectedAllergy.rxnormCUI || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Term Type
                  </Typography>
                  <Typography variant="body2">
                    {selectedAllergy.termType || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Source Vocabulary
                  </Typography>
                  <Typography variant="body2">
                    {selectedAllergy.sourceVocabulary || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Data Source
                  </Typography>
                  <Typography variant="body2">
                    {selectedAllergy.dataSource || 'N/A'} (v{selectedAllergy.version || 'N/A'})
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Allergy Categories
              </Typography>
              
              {selectedAllergy.allergyCategories.map((category, index) => (
                <Accordion 
                  key={index} 
                  defaultExpanded={index === 0}
                  sx={{ 
                    '&:before': { display: 'none' },
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    mb: 1,
                    '&:last-of-type': { mb: 0 }
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        label={category.category} 
                        color={getSeverityColor(category.severity)}
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {category.description}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Cross-reactive drugs
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {category.crossReactiveDrugs.map((drug, idx) => (
                          <Chip key={idx} label={drug} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Common reactions
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {category.commonReactions.map((reaction, idx) => (
                          <Chip key={idx} label={reaction} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default AllergiesMetadataViewer; 